import { motion, HTMLMotionProps, Variants } from 'framer-motion';
import { ReactNode, useRef } from 'react';
import { useInView } from 'framer-motion';
import { slideUp, fadeIn, scaleIn } from '@/lib/motion/variants';
import { useReducedMotion } from '@/lib/motion/hooks/use-reduced-motion';

type AnimationType = 'slide' | 'fade' | 'scale';

const animationVariants: Record<AnimationType, Variants> = {
  slide: slideUp,
  fade: fadeIn,
  scale: scaleIn,
};

interface ViewportRevealProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  children: ReactNode;
  className?: string;
  /** Animation type */
  animation?: AnimationType;
  /** Trigger only once */
  once?: boolean;
  /** Viewport margin (negative brings trigger earlier) */
  margin?: string | { top?: number; right?: number; bottom?: number; left?: number };
  /** Amount of element that must be visible (0-1) */
  amount?: number | 'some' | 'all';
  /** Delay before animation starts */
  delay?: number;
}

export function ViewportReveal({
  children,
  className,
  animation = 'slide',
  once = true,
  margin = '-50px',
  amount = 0.3,
  delay = 0,
  ...props
}: ViewportRevealProps) {
  const reducedMotion = useReducedMotion();
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin: margin as any, amount });

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  const variants = animationVariants[animation];

  return (
    <motion.div
      ref={ref}
      variants={variants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      transition={{ delay }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}
